import dynamic from 'next/dynamic';
import { usePlausible } from 'next-plausible';
import { useEffect, useState } from 'react';
import { getUser } from '../../../helpers/token';
import { figtree, fraunces } from '../../Landing/fonts';
import { ArrowIcon, CheckItem, Stamp } from '../../Landing/ui';
import FutureImage from '../../FutureImage';

// Signup opens the boarding-pass JoinDialog (via LoginButton), never the
// legacy /join page; lazy so the dialog stack stays out of the post bundle.
const LoginButton = dynamic(
  () => import('../../Header/LoginButton'),
  { ssr: false },
);

// Destination-aware invitation to start a blog. The mock browser window
// reuses THIS post's cover, so the reader literally sees their kind of
// story on their own domain. Rendered in the static HTML (so search
// visitors always see it) and removed on mount for logged-in members.
export default function StartBlogCta({ country, thumbUrl }) {
  const [hidden, setHidden] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const plausible = usePlausible();

  useEffect(() => {
    if (getUser()) setHidden(true);
  }, []);

  if (hidden) return null;

  return (
    <aside
      className={`${figtree.className} tw-mx-auto tw-mb-12 tw-mt-14 tw-max-w-4xl tw-px-4`}
    >
      <div className="tw-relative tw-rounded-2xl tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-width:1.5px] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-p-7 tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)] sm:tw-p-9">
        <Stamp
          className="tw-absolute -tw-right-2 -tw-top-6 tw-h-20 tw-w-20 tw-rotate-12 tw-text-amber-600/80 dark:tw-text-amber-500/80 sm:-tw-right-4"
          text="TRAVELFEED · YOUR TURN · "
        />
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-8 md:tw-flex-row">
          <div className="tw-min-w-0 tw-flex-1">
            <p className="tw-m-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300">
              Your turn
            </p>
            <p
              className={`${fraunces.className} tw-mb-0 tw-mt-3 tw-text-2xl tw-font-semibold tw-leading-snug tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] sm:tw-text-[1.85rem]`}
            >
              {country ? (
                <>
                  Your{' '}
                  <em className="tw-italic tw-text-primary-700 dark:tw-text-primary-300">
                    {country}
                  </em>{' '}
                  story deserves a home like this.
                </>
              ) : (
                <>Your travel stories deserve a home like this.</>
              )}
            </p>
            <p className="tw-mb-0 tw-mt-3 tw-max-w-xl tw-text-[15px] tw-leading-relaxed tw-text-[#5F6F69] dark:tw-text-[#93A69E]">
              This blog is published with TravelFeed. You could start yours in
              minutes, no tech skills needed.
            </p>
            <ul className="tw-m-0 tw-mt-4 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
              <CheckItem>Your own domain and a design this beautiful</CheckItem>
              <CheckItem>AI writing tools, maps and photo galleries</CheckItem>
              <CheckItem>
                A community of real travel bloggers from day one
              </CheckItem>
            </ul>
            <div className="tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-gap-4">
              <button
                type="button"
                onClick={() => {
                  plausible('CTA: Start Blog Card', { props: { country } });
                  setJoinOpen(true);
                }}
                className="tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-primary-600 tw-px-6 tw-py-3 tw-text-[15px] tw-font-semibold tw-text-white tw-no-underline tw-shadow-[0_10px_30px_-10px_rgba(0,137,123,0.7)] tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 hover:tw-bg-primary-700 hover:tw-text-white"
              >
                Start your travel blog
                <ArrowIcon />
              </button>
              <span className="tw-font-mono tw-text-[11px] tw-tracking-wide tw-text-[#8A958F]">
                From $19/year · Online in minutes
              </span>
            </div>
          </div>

          {/* Mock browser: the reader's future blog */}
          {thumbUrl && (
            <div
              aria-hidden="true"
              className="tw-hidden tw-w-[17rem] tw-shrink-0 tw-rotate-1 md:tw-block"
            >
              <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#3A4D44] tw-bg-white dark:tw-bg-[#0F1D18] tw-shadow-[0_20px_45px_-22px_rgba(29,45,40,0.55)]">
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-border-0 tw-border-b tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-[#FAF8F1] dark:tw-bg-[#15231E] tw-px-3 tw-py-2">
                  <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-[#E8A8A2]" />
                  <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-[#EFD49B]" />
                  <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-[#A8CCB4]" />
                  <span className="tw-ml-2 tw-flex-1 tw-rounded-full tw-bg-white dark:tw-bg-[#0F1D18] tw-px-2.5 tw-py-0.5 tw-text-center tw-font-mono tw-text-[9px] tw-tracking-wide tw-text-[#8A958F]">
                    your-name.com
                  </span>
                </div>
                <div className="tw-relative tw-aspect-[16/10]">
                  <FutureImage
                    src={thumbUrl}
                    alt=""
                    fill
                    sizes="272px"
                    maxSrcSetWidth={800}
                    className="tw-object-cover"
                  />
                </div>
                <div className="tw-px-4 tw-py-3.5">
                  <p
                    className={`${fraunces.className} tw-m-0 tw-text-[13px] tw-font-semibold tw-leading-snug tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]`}
                  >
                    Your story, beautifully told
                  </p>
                  <div className="tw-mt-2.5 tw-space-y-1.5">
                    <div className="tw-h-1.5 tw-w-full tw-rounded tw-bg-[#E7E0CF] dark:tw-bg-[#26382F]" />
                    <div className="tw-h-1.5 tw-w-4/5 tw-rounded tw-bg-[#E7E0CF] dark:tw-bg-[#26382F]" />
                    <div className="tw-h-1.5 tw-w-3/5 tw-rounded tw-bg-[#E7E0CF] dark:tw-bg-[#26382F]" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {joinOpen && (
        <LoginButton
          open
          hideButtons
          onClickClose={() => setJoinOpen(false)}
        />
      )}
    </aside>
  );
}
